import React, { useState, useCallback, useMemo } from 'react';
import { Box, Button, Typography, TextField } from '@mui/material';
import SaveButton from 'components/buttonsComponent/SaveButton';

// Constants
const FORM_WIDTH = 707;
const FIELD_STYLES = {
  textarea: {
    '& .MuiOutlinedInput-root': {
      fontFamily: 'DM Sans',
      fontSize: '14px',
      '& fieldset': {
        borderColor: '#ccc',
      },
      '&:hover fieldset': {
        borderColor: '#999',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#02462E',
      },
    },
  },
};

const FreeTextInputComponent = ({
  stepData,
  onNext,
  onPrevious,
  onSave,
  nextButtonText = "Next",
  previousButtonText = "Previous",
  showSaveButton = true,
  showNextButton = true,
  showPreviousButton = false,
  titleColor = '#02462E',
  subtitleColor = '#02462E',
  logoSrc = "/logo.png",
  logoAlt = "ESG Vision",
  formWidth = FORM_WIDTH,
}) => {
  const [responses, setResponses] = useState({});

  // Memoized handlers
  const handleNext = useCallback(() => {
    onNext?.(responses);
  }, [onNext, responses]);

  const handlePrevious = useCallback(() => {
    onPrevious?.();
  }, [onPrevious]);

  const handleSave = useCallback(() => {
    onSave?.(responses);
  }, [onSave, responses]);

  const handleInputChange = useCallback((promptId, value) => {
    setResponses(prev => ({ ...prev, [promptId]: value }));
  }, []);

  // Memoized styles
  const containerStyles = useMemo(() => ({
    width: { xs: '100%', sm: '90%', md: `${formWidth}px` },
    margin: '0 auto',
    padding: { xs: 2, sm: 3, md: 4 },
    backgroundColor: '#fff',
    borderRadius: '12px',
    maxHeight: '70vh',
    overflowY: 'auto',
  }), [formWidth]);

  const buttonStyles = useMemo(() => ({
    background: '#02462E',
    color: '#fff',
    '&:hover': {
      background: '#023d28',
    },
    textTransform: 'none',
    width: formWidth,
    height: '64px',
    borderRadius: '10px',
    fontFamily: 'DM Sans',
    fontSize: '24px',
    fontWeight: '700',
  }), [formWidth]);

  const secondaryButtonStyles = useMemo(() => ({
    background: 'transparent',
    color: '#02462E',
    border: '2px solid #02462E',
    '&:hover': {
      background: 'rgba(53, 65, 165, 0.1)',
    },
    textTransform: 'none',
    width: formWidth,
    height: '64px',
    borderRadius: '10px',
    fontFamily: 'DM Sans',
    fontSize: '24px',
    fontWeight: '700',
  }), [formWidth]);

  if (!stepData) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="error">
          No step data provided
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Header */}
      <Box
        className="standards-header"
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
          [theme => theme.breakpoints.down('sm')]: {
            justifyContent: 'center',
            flexDirection: 'column',
            alignItems: 'stretch',
          },
        }}
      >
        {showSaveButton && (
          <SaveButton onClick={handleSave}>Save</SaveButton>
        )}
      </Box>

      {/* Title */}
      <Typography
        variant="h6"
        className="mb-4 mx-4"
        style={{
          color: titleColor,
          fontWeight: 'bold',
          mb: 2,
          alignItems: 'center',
          fontFamily: 'DM Sans',
          fontSize: '24px',
          lineHeight: '42px'
        }}
      >
        {stepData.title}
      </Typography>

      {/* Subtitle */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Typography
          variant="h6"
          className="my-4"
          sx={{
            color: subtitleColor,
            fontWeight: 'bold',
            fontFamily: 'DM Sans',
            fontSize: '24px',
            lineHeight: '42px',
          }}
        >
          {stepData.subtitle}
        </Typography>
      </Box>

      {/* Form Container */}
      <Box sx={containerStyles}>
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: 'bold',
            mb: 3,
            fontFamily: 'DM Sans',
          }}
        >
          Free Text Prompts:
        </Typography>

        {/* Free Text Prompts */}
        {stepData.prompts?.map((prompt, index) => (
          <Box key={prompt.id} mb={4}>
            <Typography
              sx={{
                fontWeight: '500',
                fontSize: '16px',
                fontFamily: 'DM Sans',
                mb: 2,
                color: '#333',
              }}
            >
              • {prompt.question}
            </Typography>
            
            <TextField
              fullWidth
              multiline
              rows={prompt.rows || 3}
              variant="outlined"
              value={responses[prompt.id] || ''}
              onChange={(e) => handleInputChange(prompt.id, e.target.value)}
              placeholder={prompt.placeholder}
              sx={{
                ...FIELD_STYLES.textarea,
                mb: 2,
              }}
            />
          </Box>
        ))}
      </Box>

      {/* Action Buttons */}
      <Box
        sx={{
          mt: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        {showPreviousButton && (
          <Button
            variant="outlined"
            sx={secondaryButtonStyles}
            onClick={handlePrevious}
          >
            {previousButtonText}
          </Button>
        )}

        {showNextButton && (
          <Button
            variant="contained"
            sx={buttonStyles}
            onClick={handleNext}
          >
            {nextButtonText}
          </Button>
        )}
      </Box>
    </>
  );
};

export default FreeTextInputComponent;